/**
 * Operator-editable Organic Content knobs. Defaults match the shipped desk.
 * Persist is YAML in this browser. No tenant-config write API exists yet.
 */

import { useSyncExternalStore } from 'react';

import { SPARC_ORGANIC_DRIVE_FOLDER_ID } from './organicDriveCorpus';

export const ORGANIC_DESK_SETTINGS_PATH = '/rfp/desk-settings';
export const ORGANIC_DESK_SETTINGS_STORAGE_KEY = 'sparc-organic-desk-settings-v1';

export const ORGANIC_DESK_IDS = [
  'geoai',
  'esg',
  'sovereign',
  'mine',
  'agri',
  'forest',
  'defence',
  'procurement',
] as const;

export type OrganicDeskId = (typeof ORGANIC_DESK_IDS)[number];

export const ORGANIC_DESK_PRESET_IDS = [
  'default',
  'defence-heavy',
  'mine-agri-fill',
  'tight-keep',
] as const;

export type OrganicDeskPresetId = (typeof ORGANIC_DESK_PRESET_IDS)[number];

export type OrganicDeskRow = {
  keywords: string[];
  productName: string;
  feedFloor: number;
  xTemplate: string;
  linkedinTemplate: string;
  insightsDekTemplate: string;
  whyTemplate: string;
};

export type OrganicChannelLimit = {
  max: number;
  feedCut: number;
};

export type OrganicChannelLimits = {
  x: OrganicChannelLimit;
  linkedin: OrganicChannelLimit;
  insightsTitle: OrganicChannelLimit;
  insightsDek: OrganicChannelLimit;
  beehiivHeadline: OrganicChannelLimit;
};

export type OrganicAblationWeights = {
  virality: number;
  relevance: number;
  nuance: number;
  contrarian: number;
};

export const SUGGESTED_PACK_IDS = ['editorial', 'close', 'surprising'] as const;
export type SuggestedPackId = (typeof SUGGESTED_PACK_IDS)[number];

export function isSuggestedPackId(value: unknown): value is SuggestedPackId {
  return typeof value === 'string' && (SUGGESTED_PACK_IDS as readonly string[]).includes(value);
}

export type OrganicDeskSettings = {
  preset: OrganicDeskPresetId;
  feedFloor: number;
  recencyWindowDays: number;
  cacheRetentionDays: number;
  documentsPerCluster: number;
  boardSlotsPerDesk: number;
  suggestedPack: SuggestedPackId;
  composeFromBoard: boolean;
  feedCategoryPrior: number;
  storiesPerCluster: number;
  sameClusterBonus: number;
  overlapWeight: number;
  zoomMin: number;
  zoomMax: number;
  labelZoom: number;
  ablationWeights: OrganicAblationWeights;
  categoryToDesk: Record<string, OrganicDeskId>;
  commodityPatterns: {
    mine: string;
    agri: string;
    forest: string;
    esg: string;
  };
  desks: Record<OrganicDeskId, OrganicDeskRow>;
  channelLimits: OrganicChannelLimits;
  driveFolderId: string;
};

const X_TEMPLATE = '{title}. {capability}.';
const LINKEDIN_TEMPLATE = '{title}\n\n{capability}. {win}';
const INSIGHTS_DEK_TEMPLATE = '{capability}. {win}';
const WHY_TEMPLATE = '{capability}. {win}';

function deskRow(
  keywords: string[],
  productName: string,
  feedFloor = 8,
): OrganicDeskRow {
  return {
    keywords,
    productName,
    feedFloor,
    xTemplate: X_TEMPLATE,
    linkedinTemplate: LINKEDIN_TEMPLATE,
    insightsDekTemplate: INSIGHTS_DEK_TEMPLATE,
    whyTemplate: WHY_TEMPLATE,
  };
}

export const DEFAULT_ORGANIC_DESK_SETTINGS: OrganicDeskSettings = {
  preset: 'default',
  feedFloor: 8,
  recencyWindowDays: 30,
  cacheRetentionDays: 180,
  documentsPerCluster: 50,
  boardSlotsPerDesk: 3,
  suggestedPack: 'editorial',
  composeFromBoard: true,
  feedCategoryPrior: 0.05,
  storiesPerCluster: 40,
  sameClusterBonus: 0.55,
  overlapWeight: 0.7,
  zoomMin: 0.6,
  zoomMax: 3.5,
  labelZoom: 1.4,
  ablationWeights: {
    virality: 0.25,
    relevance: 0.25,
    nuance: 0.25,
    contrarian: 0.25,
  },
  categoryToDesk: {
    agriculture: 'agri',
    forestry: 'forest',
    mining: 'mine',
    defense: 'defence',
    geopolitics: 'defence',
    osint: 'defence',
    government_contracts: 'procurement',
    government_technology: 'sovereign',
    procurement_usa: 'procurement',
    policy: 'sovereign',
    space: 'geoai',
    technology: 'geoai',
    market: 'esg',
  },
  commodityPatterns: {
    mine: String.raw`\b(mineral|lithium|copper|iron ore|gold|silver|uranium|rare earth|neodymium|metal|kitco|bhp|ore)\b`,
    agri: String.raw`\b(grain|wheat|corn|soy|farmer|agri|fao|giews)\b`,
    forest: String.raw`\b(timber|lumber|wood|lesprom)\b`,
    esg: String.raw`\b(oil|energy|gas|eia|rigzone)\b`,
  },
  desks: {
    geoai: deskRow(
      [
        'geoai',
        'geospatial',
        'gis',
        'satellite',
        'earth observation',
        'remote sensing',
        'change detection',
        'sar',
        'ndvi',
        'volcano',
        'eruption',
        'ash',
        'flood',
        'earthquake',
        'wildfire',
        'evacuation',
        'hazard',
        'displacement',
        'tsunami',
        'landslide',
        'insar',
        'coherence',
        'mosaic',
      ],
      'SPARC GeoAI',
    ),
    esg: deskRow(
      [
        'esg',
        'climate',
        'compliance',
        'environmental assessment',
        'contaminated',
        'emissions',
        'mrv',
        'volcano',
        'eruption',
        'ash',
        'flood',
        'earthquake',
        'wildfire',
        'evacuation',
        'hazard',
        'displacement',
        'mrv protocol',
        'scope 1',
      ],
      'SPARC environmental monitoring',
    ),
    sovereign: deskRow(
      [
        'infrastructure',
        'digital government',
        'govearner',
        'public sector',
        'policy',
        'sovereign',
        'ports',
        'spaceport',
        'launch',
        'grid',
        'subsea',
        'ministry',
        'public works',
        'critical infrastructure',
        'ground station',
        'gnss',
        'logistics yard',
        'data centre',
      ],
      'GovEarner',
    ),
    mine: deskRow(
      [
        'mining',
        'mine',
        'tailings',
        'pit',
        'ore',
        'mineral',
        'tailings dam',
        'slurry',
        'leach',
        'overburden',
        'haul road',
        'closure',
        'amd',
        'copper',
        'lithium',
        'rare earth',
        'rehabilitation',
      ],
      'MineScope',
    ),
    agri: deskRow(
      [
        'agriculture',
        'crop',
        'farmer',
        'agri',
        'ndvi',
        'flood',
        'drought',
        'irrigation',
        'yield',
        'soil moisture',
        'crop insurance',
        'hail',
        'frost',
        'locust',
        'pasture',
        'livestock',
        'planting',
        'harvest',
        'salinity',
        'food security',
        'grain',
        'wheat',
        'fertilizer',
        'fallow',
        'phenology',
        'rangeland',
      ],
      'AgriScope',
    ),
    forest: deskRow(
      [
        'forestry',
        'forest',
        'timber',
        'canopy',
        'eudr',
        'restoration',
        'catchment',
        'woodland',
        'wildfire',
        'fire',
        'deforestation',
        'illegal logging',
        'eudr parcel',
        'peat',
        'fuel load',
        'prescribed burn',
        'bark beetle',
        'concession',
        'redd',
        'smoke',
        'salvage',
        'logging road',
        'biomass',
        'reforestation',
      ],
      'ForestScope',
    ),
    defence: deskRow(
      [
        'defense',
        'defence',
        'maritime',
        'geoint',
        'unclassified',
        'ais',
        'coastal',
        'isr',
        'border',
        'nato',
        'osint',
        'military',
        'corridor',
        'range',
        'basing',
        'sealift',
        'uav',
        'domain awareness',
        'airlift',
        'littoral',
        'theatre',
        'patrol',
      ],
      'DefenseScope',
    ),
    procurement: deskRow(
      [
        'tender',
        'rfp',
        'solicitation',
        'sam',
        'jaggaer',
        'contract vehicle',
        'procurement',
        'federal contract',
        'govscope',
        'municipal',
        'cadastre',
        'rfi',
        'rfq',
        'idiq',
        'gsa',
        'standing offer',
        'evaluation criteria',
        'earth observation sow',
        'municipal gis',
        'award notice',
        'source selection',
        'far',
        'bid',
        'standing arrangement',
      ],
      'GovScope',
    ),
  },
  channelLimits: {
    x: { max: 280, feedCut: 280 },
    linkedin: { max: 3000, feedCut: 900 },
    insightsTitle: { max: 220, feedCut: 220 },
    insightsDek: { max: 800, feedCut: 800 },
    beehiivHeadline: { max: 220, feedCut: 180 },
  },
  driveFolderId: SPARC_ORGANIC_DRIVE_FOLDER_ID,
};

export const ORGANIC_DESK_PRESETS: Record<
  OrganicDeskPresetId,
  { label: string; hint: string }
> = {
  default: {
    label: 'Default',
    hint: 'Shipped floors, keywords, and pairing weights.',
  },
  'defence-heavy': {
    label: 'Defence-heavy',
    hint: 'More Defence feeds and keywords. Longer recency window.',
  },
  'mine-agri-fill': {
    label: 'Mine-Agri fill',
    hint: 'Higher Mine, Agri, and Forest feed floors.',
  },
  'tight-keep': {
    label: 'Tight keep',
    hint: 'Fewer feeds and stories. Stronger category prior.',
  },
};

function cloneSettings(value: OrganicDeskSettings): OrganicDeskSettings {
  return JSON.parse(JSON.stringify(value)) as OrganicDeskSettings;
}

function isDeskId(value: string): value is OrganicDeskId {
  return (ORGANIC_DESK_IDS as readonly string[]).includes(value);
}

function migrateDeskId(value: string): OrganicDeskId | null {
  if (value === 'land') return 'procurement';
  return isDeskId(value) ? value : null;
}

function isPresetId(value: string): value is OrganicDeskPresetId {
  return (ORGANIC_DESK_PRESET_IDS as readonly string[]).includes(value);
}

function asNumber(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function asStringList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return [...fallback];
  const list = value
    .map((item) => String(item).trim())
    .filter((item) => item.length > 0);
  return list.length > 0 ? list : [...fallback];
}

function mergeDeskRow(base: OrganicDeskRow, patch: Partial<OrganicDeskRow> | undefined): OrganicDeskRow {
  if (!patch) return { ...base, keywords: [...base.keywords] };
  return {
    keywords: asStringList(patch.keywords, base.keywords),
    productName: typeof patch.productName === 'string' && patch.productName.trim()
      ? patch.productName.trim()
      : base.productName,
    feedFloor: asNumber(patch.feedFloor, base.feedFloor, 0, 40),
    xTemplate: migrateTemplatePlaceholders(
      typeof patch.xTemplate === 'string' && patch.xTemplate.trim()
        ? patch.xTemplate
        : base.xTemplate,
    ),
    linkedinTemplate: migrateTemplatePlaceholders(
      typeof patch.linkedinTemplate === 'string' && patch.linkedinTemplate.trim()
        ? patch.linkedinTemplate
        : base.linkedinTemplate,
    ),
    insightsDekTemplate: migrateTemplatePlaceholders(
      typeof patch.insightsDekTemplate === 'string' && patch.insightsDekTemplate.trim()
        ? patch.insightsDekTemplate
        : base.insightsDekTemplate,
    ),
    whyTemplate: migrateTemplatePlaceholders(
      typeof patch.whyTemplate === 'string' && patch.whyTemplate.trim()
        ? patch.whyTemplate
        : base.whyTemplate,
    ),
  };
}

export function mergeOrganicDeskSettings(
  base: OrganicDeskSettings,
  patch: Partial<OrganicDeskSettings> | Record<string, unknown>,
): OrganicDeskSettings {
  const next = cloneSettings(base);
  const raw = patch as Partial<OrganicDeskSettings>;
  if (typeof raw.preset === 'string' && isPresetId(raw.preset)) next.preset = raw.preset;
  next.feedFloor = asNumber(raw.feedFloor, next.feedFloor, 0, 40);
  next.recencyWindowDays = asNumber(raw.recencyWindowDays, next.recencyWindowDays, 1, 90);
  next.cacheRetentionDays = asNumber(
    (raw as { cacheRetentionDays?: unknown }).cacheRetentionDays,
    next.cacheRetentionDays,
    1,
    365,
  );
  next.documentsPerCluster = asNumber(
    (raw as { documentsPerCluster?: unknown }).documentsPerCluster,
    next.documentsPerCluster,
    1,
    80,
  );
  next.boardSlotsPerDesk = asNumber(
    (raw as { boardSlotsPerDesk?: unknown }).boardSlotsPerDesk,
    next.boardSlotsPerDesk,
    1,
    8,
  );
  if (isSuggestedPackId((raw as { suggestedPack?: unknown }).suggestedPack)) {
    next.suggestedPack = (raw as { suggestedPack: SuggestedPackId }).suggestedPack;
  }
  if (typeof (raw as { composeFromBoard?: unknown }).composeFromBoard === 'boolean') {
    next.composeFromBoard = (raw as { composeFromBoard: boolean }).composeFromBoard;
  }
  next.feedCategoryPrior = asNumber(raw.feedCategoryPrior, next.feedCategoryPrior, 0, 1);
  next.storiesPerCluster = asNumber(raw.storiesPerCluster, next.storiesPerCluster, 1, 80);
  next.sameClusterBonus = asNumber(raw.sameClusterBonus, next.sameClusterBonus, 0, 1);
  next.overlapWeight = asNumber(raw.overlapWeight, next.overlapWeight, 0, 1);
  next.zoomMin = asNumber(raw.zoomMin, next.zoomMin, 0.2, 2);
  next.zoomMax = asNumber(raw.zoomMax, next.zoomMax, 1, 8);
  next.labelZoom = asNumber(raw.labelZoom, next.labelZoom, 0.2, 8);
  if (raw.ablationWeights) {
    next.ablationWeights = {
      virality: asNumber(raw.ablationWeights.virality, next.ablationWeights.virality, 0, 1),
      relevance: asNumber(raw.ablationWeights.relevance, next.ablationWeights.relevance, 0, 1),
      nuance: asNumber(raw.ablationWeights.nuance, next.ablationWeights.nuance, 0, 1),
      contrarian: asNumber(raw.ablationWeights.contrarian, next.ablationWeights.contrarian, 0, 1),
    };
  }
  if (raw.categoryToDesk && typeof raw.categoryToDesk === 'object') {
    const mapped: Record<string, OrganicDeskId> = { ...next.categoryToDesk };
    for (const [key, value] of Object.entries(raw.categoryToDesk)) {
      if (typeof value !== 'string') continue;
      const mappedId = migrateDeskId(value);
      if (mappedId) mapped[key] = mappedId;
    }
    next.categoryToDesk = mapped;
  }
  if (raw.commodityPatterns) {
    next.commodityPatterns = {
      mine: typeof raw.commodityPatterns.mine === 'string' ? raw.commodityPatterns.mine : next.commodityPatterns.mine,
      agri: typeof raw.commodityPatterns.agri === 'string' ? raw.commodityPatterns.agri : next.commodityPatterns.agri,
      forest: typeof raw.commodityPatterns.forest === 'string' ? raw.commodityPatterns.forest : next.commodityPatterns.forest,
      esg: typeof raw.commodityPatterns.esg === 'string' ? raw.commodityPatterns.esg : next.commodityPatterns.esg,
    };
  }
  if (raw.desks) {
    const desks = raw.desks as Record<string, Partial<OrganicDeskRow> | undefined>;
    for (const id of ORGANIC_DESK_IDS) {
      const patch = desks[id] ?? (id === 'procurement' ? desks.land : undefined);
      next.desks[id] = mergeDeskRow(next.desks[id], patch);
    }
  }
  if (raw.channelLimits) {
    const keys: Array<keyof OrganicChannelLimits> = [
      'x',
      'linkedin',
      'insightsTitle',
      'insightsDek',
      'beehiivHeadline',
    ];
    for (const key of keys) {
      const row = raw.channelLimits[key];
      if (!row) continue;
      next.channelLimits[key] = {
        max: asNumber(row.max, next.channelLimits[key].max, 20, 8000),
        feedCut: asNumber(row.feedCut, next.channelLimits[key].feedCut, 20, 8000),
      };
    }
  }
  if (typeof raw.driveFolderId === 'string' && raw.driveFolderId.trim()) {
    next.driveFolderId = raw.driveFolderId.trim();
  } else if (!next.driveFolderId) {
    next.driveFolderId = SPARC_ORGANIC_DRIVE_FOLDER_ID;
  }
  return next;
}

function yamlQuote(value: string): string {
  if (value === '' || /[:#\n]|^\s|\s$|^[-?]/.test(value) || value === 'true' || value === 'false') {
    return JSON.stringify(value);
  }
  return value;
}

function yamlDump(value: unknown, indent = 0): string {
  const pad = '  '.repeat(indent);
  if (Array.isArray(value)) {
    if (value.length === 0) return `${pad}[]\n`;
    return value
      .map((item) => {
        if (item !== null && typeof item === 'object') {
          const nested = yamlDump(item, indent + 1).replace(/^\s+/, '');
          return `${pad}- ${nested}`;
        }
        return `${pad}- ${yamlQuote(String(item))}\n`;
      })
      .join('');
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return `${pad}{}\n`;
    return entries
      .map(([key, child]) => {
        if (child !== null && typeof child === 'object') {
          return `${pad}${key}:\n${yamlDump(child, indent + 1)}`;
        }
        if (typeof child === 'number' || typeof child === 'boolean') {
          return `${pad}${key}: ${child}\n`;
        }
        return `${pad}${key}: ${yamlQuote(String(child ?? ''))}\n`;
      })
      .join('');
  }
  if (typeof value === 'number' || typeof value === 'boolean') return `${pad}${value}\n`;
  return `${pad}${yamlQuote(String(value ?? ''))}\n`;
}

function migrateTemplatePlaceholders(text: string): string {
  const next = text.replace(/\{desk\}/g, '{cluster}');
  if (/\{product\} already covers this/i.test(next)) return X_TEMPLATE;
  if (/\{product\} already maps this/i.test(next)) {
    return next.includes('\n') ? LINKEDIN_TEMPLATE : WHY_TEMPLATE;
  }
  if (/\{product\} already has a named pair/i.test(next)) return INSIGHTS_DEK_TEMPLATE;
  if (/sparcglobal\.ca\/contact|talk to SPARC|live programme/i.test(next)) {
    return next.includes('\n') ? LINKEDIN_TEMPLATE : X_TEMPLATE;
  }
  return next;
}

export function copyTemplateIsProductOnly(template: string): boolean {
  const raw = template.replace(/\{desk\}/g, '{cluster}');
  const hasProduct = /\{product\}/.test(raw);
  const hasCapability = /\{capability\}/.test(raw) || /\{win\}/.test(raw);
  return hasProduct && !hasCapability;
}

export function stringifyOrganicDeskYaml(settings: OrganicDeskSettings): string {
  return `# SPARC Organic Content settings\n# Edit in Organic settings or paste YAML here, then Save.\n${yamlDump(settings)}`;
}

function parseScalar(raw: string): unknown {
  const text = raw.trim();
  if (text === '' || text === 'null' || text === '~') return null;
  if (text === 'true') return true;
  if (text === 'false') return false;
  if (text === '[]') return [];
  if (text === '{}') return {};
  if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text);
  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    try {
      return JSON.parse(text.startsWith("'") ? `"${text.slice(1, -1).replace(/"/g, '\\"')}"` : text);
    } catch {
      return text.slice(1, -1);
    }
  }
  return text;
}

function parseYamlBlock(lines: string[], start: number, indent: number): { value: unknown; next: number } {
  const obj: Record<string, unknown> = {};
  const list: unknown[] = [];
  let isList = false;
  let index = start;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim() || line.trim().startsWith('#')) {
      index += 1;
      continue;
    }
    const match = line.match(/^(\s*)(.*)$/);
    if (!match) {
      index += 1;
      continue;
    }
    const spaces = match[1].length;
    const body = match[2];
    if (spaces < indent && body.length > 0) break;
    if (spaces > indent) {
      index += 1;
      continue;
    }
    if (body.startsWith('- ')) {
      isList = true;
      const rest = body.slice(2).trim();
      if (!rest) {
        const child = parseYamlBlock(lines, index + 1, indent + 2);
        list.push(child.value);
        index = child.next;
      } else if (rest.includes(':') && !rest.startsWith('"')) {
        const colon = rest.indexOf(':');
        const key = rest.slice(0, colon).trim();
        const after = rest.slice(colon + 1).trim();
        const row: Record<string, unknown> = {};
        if (after) {
          row[key] = parseScalar(after);
          index += 1;
        } else {
          const child = parseYamlBlock(lines, index + 1, indent + 2);
          row[key] = child.value;
          index = child.next;
        }
        const extra = parseYamlBlock(lines, index, indent + 2);
        if (extra.value && typeof extra.value === 'object' && !Array.isArray(extra.value)) {
          Object.assign(row, extra.value);
          index = extra.next;
        }
        list.push(row);
      } else {
        list.push(parseScalar(rest));
        index += 1;
      }
      continue;
    }
    const colon = body.indexOf(':');
    if (colon < 0) {
      index += 1;
      continue;
    }
    const key = body.slice(0, colon).trim();
    const after = body.slice(colon + 1).trim();
    if (after) {
      obj[key] = parseScalar(after);
      index += 1;
    } else {
      const child = parseYamlBlock(lines, index + 1, indent + 2);
      obj[key] = child.value;
      index = child.next;
    }
  }
  return { value: isList ? list : obj, next: index };
}

export function parseOrganicDeskYaml(text: string): OrganicDeskSettings {
  const trimmed = text.trim();
  if (!trimmed) return cloneSettings(DEFAULT_ORGANIC_DESK_SETTINGS);
  if (trimmed.startsWith('{')) {
    return mergeOrganicDeskSettings(DEFAULT_ORGANIC_DESK_SETTINGS, JSON.parse(trimmed) as Record<string, unknown>);
  }
  const lines = text.replace(/\t/g, '  ').split(/\r?\n/);
  const parsed = parseYamlBlock(lines, 0, 0).value as Record<string, unknown>;
  return mergeOrganicDeskSettings(DEFAULT_ORGANIC_DESK_SETTINGS, parsed);
}

function uniqueKeywords(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of list) {
    const key = item.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item.trim());
  }
  return out;
}

function patchDesks(
  settings: OrganicDeskSettings,
  ids: OrganicDeskId[],
  patch: Partial<OrganicDeskRow>,
): void {
  for (const id of ids) {
    settings.desks[id] = mergeDeskRow(settings.desks[id], patch);
  }
}

export function settingsFromPreset(preset: OrganicDeskPresetId): OrganicDeskSettings {
  const next = cloneSettings(DEFAULT_ORGANIC_DESK_SETTINGS);
  next.preset = preset;
  switch (preset) {
    case 'default':
      return next;
    case 'defence-heavy':
      next.feedCategoryPrior = 0.55;
      next.recencyWindowDays = 21;
      next.desks.defence.feedFloor = 14;
      next.desks.defence.keywords = uniqueKeywords([
        ...next.desks.defence.keywords,
        'military',
        'nato',
        'arms',
        'osint',
      ]);
      next.desks.geoai.feedFloor = 6;
      next.desks.mine.feedFloor = 6;
      next.desks.agri.feedFloor = 6;
      return next;
    case 'mine-agri-fill':
      next.feedFloor = 10;
      patchDesks(next, ['mine', 'agri', 'forest'], { feedFloor: 14 });
      next.desks.mine.keywords = uniqueKeywords([
        ...next.desks.mine.keywords,
        'copper',
        'lithium',
        'tailings dam',
      ]);
      next.desks.agri.keywords = uniqueKeywords([
        ...next.desks.agri.keywords,
        'grain',
        'wheat',
        'harvest',
      ]);
      next.desks.forest.keywords = uniqueKeywords([
        ...next.desks.forest.keywords,
        'lumber',
        'timber',
      ]);
      next.desks.defence.feedFloor = 6;
      return next;
    case 'tight-keep':
      next.feedFloor = 4;
      next.storiesPerCluster = 8;
      next.recencyWindowDays = 7;
      next.feedCategoryPrior = 0.6;
      for (const id of ORGANIC_DESK_IDS) next.desks[id].feedFloor = 4;
      return next;
    default: {
      const _exhaustive: never = preset;
      return _exhaustive;
    }
  }
}

export function applyCopyTemplate(
  template: string,
  vars: { title: string; product: string; cluster: string; capability?: string; win?: string },
): string {
  const migrated = migrateTemplatePlaceholders(template);
  const filled: Record<string, string> = {
    title: vars.title,
    product: vars.product,
    cluster: vars.cluster,
    capability: vars.capability ?? '',
    win: vars.win ?? '',
  };
  return migrated.replace(/\{(title|product|cluster|capability|win)\}/g, (_match, key: string) => {
    return filled[key] ?? '';
  });
}

export function compileCommodityPattern(pattern: string, fallback: string): RegExp {
  try {
    return new RegExp(pattern, 'i');
  } catch {
    return new RegExp(fallback, 'i');
  }
}

let current = cloneSettings(DEFAULT_ORGANIC_DESK_SETTINGS);
let yamlText = stringifyOrganicDeskYaml(current);
let revision = 0;
let hydrated = false;
const listeners = new Set<() => void>();

function emit(): void {
  revision += 1;
  listeners.forEach((listener) => listener());
}

function readStorage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(ORGANIC_DESK_SETTINGS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(text: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(ORGANIC_DESK_SETTINGS_STORAGE_KEY, text);
  } catch {
    /* ignore quota */
  }
}

function ensureHydrated(): void {
  if (hydrated) return;
  hydrated = true;
  const stored = readStorage();
  if (!stored) return;
  try {
    current = parseOrganicDeskYaml(stored);
    yamlText = stored;
  } catch {
    current = cloneSettings(DEFAULT_ORGANIC_DESK_SETTINGS);
    yamlText = stringifyOrganicDeskYaml(current);
  }
}

export function getOrganicDeskSettings(): OrganicDeskSettings {
  ensureHydrated();
  return current;
}

export function getOrganicDeskYaml(): string {
  ensureHydrated();
  return yamlText;
}

export function getOrganicDeskRevision(): number {
  ensureHydrated();
  return revision;
}

export function subscribeOrganicDeskSettings(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => listeners.delete(onStoreChange);
}

export function saveOrganicDeskSettings(next: OrganicDeskSettings): OrganicDeskSettings {
  ensureHydrated();
  current = mergeOrganicDeskSettings(DEFAULT_ORGANIC_DESK_SETTINGS, next);
  yamlText = stringifyOrganicDeskYaml(current);
  writeStorage(yamlText);
  emit();
  return current;
}

export function saveOrganicDeskYaml(text: string): OrganicDeskSettings {
  const parsed = parseOrganicDeskYaml(text);
  current = parsed;
  yamlText = stringifyOrganicDeskYaml(parsed);
  writeStorage(yamlText);
  hydrated = true;
  emit();
  return current;
}

export function applyOrganicDeskPreset(preset: OrganicDeskPresetId): OrganicDeskSettings {
  return saveOrganicDeskSettings(settingsFromPreset(preset));
}

export function resetOrganicDeskSettings(): OrganicDeskSettings {
  current = cloneSettings(DEFAULT_ORGANIC_DESK_SETTINGS);
  yamlText = stringifyOrganicDeskYaml(current);
  hydrated = true;
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(ORGANIC_DESK_SETTINGS_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  emit();
  return current;
}

export function useOrganicDeskSettings(): OrganicDeskSettings {
  return useSyncExternalStore(
    subscribeOrganicDeskSettings,
    getOrganicDeskSettings,
    () => DEFAULT_ORGANIC_DESK_SETTINGS,
  );
}

export function useOrganicDeskRevision(): number {
  return useSyncExternalStore(
    subscribeOrganicDeskSettings,
    getOrganicDeskRevision,
    () => 0,
  );
}
